import { readBestLegacy, writeBestLegacy } from '../sim/save';
import type { Simulation } from '../sim/sim';
import type { EndingRecord } from '../sim/types';
import { el } from './dom';

const HEADLINES: Record<string, string> = {
  endures: 'TOWN OUTLIVES ITS MINE',
  strip: 'COMPANY STRIPS CLAIM, MOVES ON',
  ghost: 'ANOTHER CAMP GOES QUIET',
  foreclosed: 'BANKRUPT CAMP SEIZED BY COMPANY',
};

function story(sim: Simulation, ending: EndingRecord): string[] {
  const state = sim.state;
  const town = ending.townName;
  const firstHousehold = state.households[0];
  const firstFamilyHead = firstHousehold
    ? state.peeps.find((peep) => peep.id === (firstHousehold.workerId ?? firstHousehold.spouseId))
    : undefined;
  const surname = firstFamilyHead?.name.split(' ').slice(-1)[0] ?? 'Pruitt';
  const pop = state.peeps.length;
  const kids = sim.kidsGrownUp;

  switch (ending.kind) {
    case 'endures':
      return [
        `${town.toUpperCase()}, Year Ten. — The ore gave out years ago, and the company with it. The town did not. ` +
          `Where survey stakes once stood, ${pop} souls now keep a main street that pays its own way — rail freight, honest commerce, and the stubbornness peculiar to desert towns.`,
        `Old-timers still point out the first cottage, the ${surname} place, curtains in the window same as the day the wagon arrived. ` +
          `${kids > 0 ? `${kids} of the children who learned their figures at the schoolhouse now draw wages here as grown hands.` : 'The schoolhouse bell still rings on the hour.'}`,
        `The pit is fenced now. Kids dare each other to look over the edge at the tiers their parents cut. ` +
          `Nobody digs. Nobody needs to. The town endures.`,
      ];
    case 'strip':
      return [
        `TERRITORY DESK. — The Consolidated Extraction Company confirmed the closure of its desert operation this week, ` +
          `settling handsomely with site management. The final ledger would make a banker whistle.`,
        `The buildings stand empty already. ${surname !== 'Pruitt' ? `The ${surname} family were seen loading a wagon east.` : 'The last families took the morning train.'} ` +
          `The saloon sign swings for nobody.`,
        `Sand does not hurry. It has already started on the doorsteps.`,
      ];
    case 'ghost':
      return [
        `${town.toUpperCase()}. — No paper is printed here anymore; this dispatch comes from the county seat. ` +
          `The town that once counted ${Math.max(pop, 8)} souls has gone quiet.`,
        `The mine outlived the money, or the money outlived the will. Either way the bus stopped coming, ` +
          `and a town is only ever people.`,
        `A traveler reports the schoolhouse door stands open, and the wind reads the roll.`,
      ];
    default:
      return [
        `TERRITORY DESK. — The Company has foreclosed on its desert site after repeated insolvency. ` +
          `Management's name has been entered into the industry blacklist, in the neat handwriting Legal reserves for such occasions.`,
        `The claim reverts to the Company. The people scatter, the way people do.`,
      ];
  }
}

/** THE TERRITORIAL GAZETTE — full-screen newspaper epilogue. */
export function showEpilogue(
  root: HTMLElement,
  sim: Simulation,
  snapshot: HTMLCanvasElement | null,
  onClose: () => void,
  onNewGame: () => void,
): void {
  const ending = sim.state.meta.ending;
  if (!ending) return;
  const legacy = ending.legacy;
  const isNewBest = writeBestLegacy({
    total: legacy.total,
    title: legacy.title,
    townName: ending.townName,
    week: ending.week,
  });
  const best = readBestLegacy();

  const scoreRow = (label: string, value: number): HTMLElement =>
    el('div', { class: 'gazette-score-row' }, el('span', {}, label), el('span', {}, String(value)));

  const photo = el('div', { class: 'gazette-photo' });
  if (snapshot) {
    snapshot.className = 'gazette-photo-canvas';
    photo.append(snapshot, el('div', { class: 'gazette-caption' }, `${ending.townName}, as the era closed.`));
  } else {
    photo.append(el('div', { class: 'gazette-caption' }, '[photograph unavailable]'));
  }

  const overlay = el(
    'div',
    { class: 'gazette-backdrop' },
    el(
      'div',
      { class: 'gazette' },
      el('div', { class: 'gazette-masthead' }, 'THE TERRITORIAL GAZETTE'),
      el(
        'div',
        { class: 'gazette-dateline' },
        `${ending.townName} · Week ${ending.week} · Price: two bits`,
      ),
      el('div', { class: 'gazette-headline' }, HEADLINES[ending.kind] ?? 'AN ERA ENDS'),
      el('div', { class: 'gazette-subhead' }, legacy.title),
      el(
        'div',
        { class: 'gazette-columns' },
        el('div', { class: 'gazette-story' }, ...story(sim, ending).map((paragraph) => el('p', {}, paragraph))),
        el(
          'div',
          { class: 'gazette-side' },
          photo,
          el('div', { class: 'gazette-score-title' }, 'THE LEDGER OF RECORD'),
          scoreRow('Fortune', legacy.fortune),
          scoreRow('People', legacy.people),
          scoreRow('Self-sufficiency', legacy.selfSufficiency),
          scoreRow('Town quality', legacy.townQuality),
          scoreRow('Company record', legacy.companyRecord),
          el('div', { class: 'gazette-total' }, `LEGACY  ${legacy.total.toLocaleString('en-US')}`),
          isNewBest
            ? el('div', { class: 'gazette-best' }, 'A NEW TERRITORIAL RECORD')
            : best
              ? el('div', { class: 'gazette-best muted' }, `Territorial record: ${best.total.toLocaleString('en-US')} (${best.townName})`)
              : el('span', {}),
        ),
      ),
      el(
        'div',
        { class: 'gazette-actions' },
        el('button', { class: 'modal-close', onclick: () => { overlay.remove(); onClose(); } }, 'Keep playing'),
        el('button', { class: 'modal-close secondary', onclick: () => { overlay.remove(); onNewGame(); } }, 'Start a new town'),
      ),
    ),
  );
  root.append(overlay);
}
